import { type Code, ConnectError } from "@connectrpc/connect";
import type { FC } from "react";

import { ActionableErrorMessage } from "@/components/ActionableErrorMessage";
import { Text } from "@/components/flexkit/typography/Text";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { extractActionableError, formatError } from "@/utils/errors";

type ErrorMessageProps = PropsWithClassName & {
    error: Error | string | undefined | null;
    messages?: Partial<Record<Code, string>>;
    "data-testid"?: string;
};

/**
 * Renders Error (especially ConnectError) in a human-readable way.
 *
 * When the error carries ActionableErrorDetails (backend-provided structured
 * error), renders ActionableErrorMessage with action buttons and expandable
 * raw details. Otherwise falls back to plain text via formatError().
 *
 * If no error is supplied it doesn't render anything; so no need to conditionally render
 * this component
 */
export const ErrorMessage: FC<ErrorMessageProps> = ({ error, className, messages, "data-testid": testId }) => {
    if (!error) {
        return null;
    }

    const fallBackTestId = error instanceof ConnectError ? `error-message-connect-${error.code}` : "error-message";
    const dataTestId = testId ?? fallBackTestId;

    // When the backend provides structured ActionableErrorDetails, render the
    // richer ActionableErrorMessage component (with optional action buttons).
    // This intentionally takes precedence over the `messages` prop — the backend's
    // structured user message is the canonical display text.
    const actionableError = error instanceof ConnectError ? extractActionableError(error) : null;
    if (actionableError) {
        return (
            <ActionableErrorMessage
                error={actionableError}
                rawMessage={error instanceof ConnectError ? error.rawMessage : undefined}
                className={className}
                data-testid={dataTestId}
            />
        );
    }

    return (
        <Text className={cn("text-base text-content-negative", className)} data-testid={dataTestId}>
            {formatError(error, messages)}
        </Text>
    );
};
